import {
  applyActionCode,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db, googleProvider, appleProvider } from './firebase';
import { legalConsentFirestorePatch } from './legalConsentFirestore';
import { getSessionInviteToken } from './chatSessionIdentity';
import { mergeInviteLookupPublicProfile } from './chatCryptoFirestore';

const REDIRECT_PENDING_KEY = 'googleRedirectPending';
const PENDING_GOOGLE_LEGAL_KEY = 'duvpost.pendingGoogleLegalConsent';

export function hasPendingGoogleLegalConsent() {
  try {
    return typeof window !== 'undefined' && sessionStorage.getItem(PENDING_GOOGLE_LEGAL_KEY) === '1';
  } catch {
    return false;
  }
}

function setPendingGoogleLegalConsent() {
  try {
    sessionStorage.setItem(PENDING_GOOGLE_LEGAL_KEY, '1');
  } catch {
    /* no-op */
  }
}

export function clearPendingGoogleLegalConsent() {
  try {
    sessionStorage.removeItem(PENDING_GOOGLE_LEGAL_KEY);
  } catch {
    /* no-op */
  }
}

async function userNeedsInitialLegalConsent(uid) {
  const ref = doc(db, 'users', uid);
  let snap;
  try {
    snap = await getDoc(ref, { source: 'server' });
  } catch {
    snap = await getDoc(ref);
  }
  if (!snap.exists()) {
    // onAuthStateChanged может прийти до первого setDoc в registerWithEmail — документа ещё нет.
    // Для email/password это не «вошли через Google без документов», а гонка; PostGoogleLegalConsent не показываем.
    try {
      const u = auth.currentUser;
      if (
        u &&
        u.uid === uid &&
        (u.providerData || []).some((p) => p.providerId === 'password')
      ) {
        return false;
      }
    } catch {
      /* no-op */
    }
    return true;
  }
  const d = snap.data();
  const tv = d.acceptedTermsVersion;
  const pv = d.acceptedPrivacyVersion;
  return typeof tv !== 'string' || !tv.trim() || typeof pv !== 'string' || !pv.trim();
}

/** После Google popup/redirect: флаг в sessionStorage, если в users/{uid} ещё нет принятых версий документов. */
export async function syncGoogleLegalConsentPendingAfterSignIn(uid) {
  if (!uid) return;
  try {
    const needs = await userNeedsInitialLegalConsent(uid);
    if (needs) setPendingGoogleLegalConsent();
    else clearPendingGoogleLegalConsent();
  } catch {
    setPendingGoogleLegalConsent();
  }
}

/** Запись принятых версий Terms/Privacy (после чекбоксов). */
export async function applyUserLegalConsentInFirestore(uid) {
  if (!uid) throw new Error('User id required');
  await setDoc(doc(db, 'users', uid), { ...legalConsentFirestorePatch() }, { merge: true });
  clearPendingGoogleLegalConsent();
}

function setGoogleRedirectPending() {
  try {
    sessionStorage.setItem(REDIRECT_PENDING_KEY, '1');
  } catch {
    /* no-op */
  }
}

function clearGoogleRedirectPending() {
  try {
    sessionStorage.removeItem(REDIRECT_PENDING_KEY);
  } catch {
    /* no-op */
  }
}

export function hasGoogleRedirectPending(): boolean {
  try {
    return sessionStorage.getItem(REDIRECT_PENDING_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Origin для continueUrl в письмах (подтверждение email и т.д.):
 * при сборке можно задать REACT_APP_FIREBASE_AUTH_CONTINUE_ORIGIN=https://duvpost.com,
 * чтобы continueUrl в письме всегда указывал на прод, а не на localhost.
 */
function getAuthContinueOrigin() {
  const fromEnv = process.env.REACT_APP_FIREBASE_AUTH_CONTINUE_ORIGIN;
  const trimmed = typeof fromEnv === 'string' ? fromEnv.trim() : '';
  if (trimmed) return trimmed.replace(/\/$/, '');
  if (typeof window !== 'undefined') return window.location.origin.replace(/\/$/, '');
  return '';
}

/**
 * Ссылка из письма ведёт в SPA (`/verify-email`); домен должен быть в Authorized domains.
 * Хост обработчика в письме: см. getFirebaseAuthActionHandlerUrl() — https://<authDomain>/__/auth/action
 */
export function buildEmailVerificationActionCodeSettings() {
  const origin = getAuthContinueOrigin();
  if (!origin) return undefined;
  return {
    url: `${origin}/verify-email`,
    handleCodeInApp: true,
  };
}

/** Action URL писем Auth: `https://<authDomain>/__/auth/action` (шаблоны в консоли Firebase). */
export { getFirebaseAuthActionHandlerUrl, getFirebaseAuthDomain } from './firebase';

export async function completeEmailVerificationFromLink(oobCode) {
  try {
    await applyActionCode(auth, oobCode);
    return { error: null, code: null };
  } catch (e) {
    return {
      error: e.message || 'Invalid or expired link',
      code: e.code || null,
    };
  }
}

/** Ссылка «Сброс пароля» (?mode=resetPassword&oobCode=...) — не путать с verifyEmail (там applyActionCode). */
export async function completePasswordResetFromLink(oobCode, newPassword) {
  try {
    await confirmPasswordReset(auth, oobCode, newPassword);
    return { error: null, code: null };
  } catch (e) {
    return {
      error: e.message || 'Invalid or expired link',
      code: e.code || null,
    };
  }
}

/** Email/password до подтверждения письма — не пускаем в приложение. */
export function userNeedsEmailVerification(user) {
  if (!user || user.emailVerified) return false;
  return (user.providerData || []).some((p) => p.providerId === 'password');
}

export async function sendVerificationEmailToCurrentUser() {
  const user = auth.currentUser;
  if (!user) return { error: 'Not signed in' };
  if (user.emailVerified) return { error: null };
  try {
    const settings = buildEmailVerificationActionCodeSettings();
    if (settings) {
      await sendEmailVerification(user, settings);
    } else {
      await sendEmailVerification(user);
    }
    return { error: null };
  } catch (e) {
    return { error: e.message || 'Failed to send verification email' };
  }
}

// Регистрация с email/password
export const registerWithEmail = async (email, password, displayName) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Обновляем профиль пользователя
    await updateProfile(user, {
      displayName: displayName || email.split('@')[0]
    });

    // Создаем документ пользователя в Firestore
    await setDoc(doc(db, 'users', user.uid), {
      uid: user.uid,
      email: user.email,
      displayName: displayName || email.split('@')[0],
      photoURL: user.photoURL || null,
      createdAt: new Date().toISOString(),
      lastLoginAt: new Date().toISOString(),
      displayNameOnboardingDone: false,
      preferences: {
        language: 'en',
        currency: 'USD',
        notifications: true
      },
      ...legalConsentFirestorePatch(),
    });

    let verificationError = null;
    try {
      const settings = buildEmailVerificationActionCodeSettings();
      if (settings) {
        await sendEmailVerification(user, settings);
      } else {
        await sendEmailVerification(user);
      }
    } catch (e) {
      verificationError = e.message || 'Verification email failed';
      console.warn('[registerWithEmail] sendEmailVerification', e);
    }

    await syncGoogleLegalConsentPendingAfterSignIn(user.uid);

    return { user, error: null, verificationError };
  } catch (error) {
    return { user: null, error: error.message };
  }
};

// Вход с email/password
export const loginWithEmail = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Обновляем время последнего входа
    await setDoc(doc(db, 'users', user.uid), {
      lastLoginAt: new Date().toISOString()
    }, { merge: true });

    return { user, error: null, firebaseCode: null };
  } catch (error) {
    return {
      user: null,
      error: error.message,
      firebaseCode: error.code || null,
    };
  }
};

// Вход через Google: popup в приоритете (устойчивее в мобильных webview), redirect — fallback.
export const loginWithGoogle = async () => {
  try {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result?.user;
      if (!user) return { user: null, error: 'Google sign-in failed', needsLegalConsent: false };
      await saveOrUpdateUser(user);
      await syncGoogleLegalConsentPendingAfterSignIn(user.uid);
      clearGoogleRedirectPending();
      return { user, error: null, needsLegalConsent: hasPendingGoogleLegalConsent() };
    } catch (popupError) {
      const popupErrorCodes = [
        'auth/popup-blocked',
        'auth/popup-closed-by-user',
        'auth/cancelled-popup-request',
      ];
      const canFallbackToRedirect =
        popupErrorCodes.includes(popupError.code) ||
        popupError.message?.includes('Cross-Origin-Opener-Policy') ||
        popupError.message?.includes('window.closed');
      if (!canFallbackToRedirect) throw popupError;
      setGoogleRedirectPending();
      await signInWithRedirect(auth, googleProvider);
      return { user: null, error: null, redirect: true, needsLegalConsent: false };
    }
  } catch (error) {
    clearGoogleRedirectPending();
    return {
      user: null,
      error: error.message || 'Ошибка входа через Google',
      needsLegalConsent: false,
    };
  }
};

// Вход через Apple: тот же поток, что у Google (Firestore user, юр. согласие при необходимости).
export const loginWithApple = async () => {
  try {
    try {
      const result = await signInWithPopup(auth, appleProvider);
      const user = result?.user;
      if (!user) return { user: null, error: 'Apple sign-in failed', needsLegalConsent: false };
      await saveOrUpdateUser(user);
      await syncGoogleLegalConsentPendingAfterSignIn(user.uid);
      clearGoogleRedirectPending();
      return { user, error: null, needsLegalConsent: hasPendingGoogleLegalConsent() };
    } catch (popupError) {
      const popupErrorCodes = [
        'auth/popup-blocked',
        'auth/popup-closed-by-user',
        'auth/cancelled-popup-request',
      ];
      const canFallbackToRedirect =
        popupErrorCodes.includes(popupError.code) ||
        popupError.message?.includes('Cross-Origin-Opener-Policy') ||
        popupError.message?.includes('window.closed');
      if (!canFallbackToRedirect) throw popupError;
      setGoogleRedirectPending();
      await signInWithRedirect(auth, appleProvider);
      return { user: null, error: null, redirect: true, needsLegalConsent: false };
    }
  } catch (error) {
    clearGoogleRedirectPending();
    return {
      user: null,
      error: error.message || 'Ошибка входа через Apple',
      needsLegalConsent: false,
    };
  }
};

// Обработка результата redirect после возврата
export const handleGoogleRedirect = async () => {
  try {
    const result = await getRedirectResult(auth);
    if (result && result.user) {
      await saveOrUpdateUser(result.user);
      await syncGoogleLegalConsentPendingAfterSignIn(result.user.uid);
      clearGoogleRedirectPending();
      return { user: result.user, error: null };
    }
    return { user: null, error: null };
  } catch (error) {
    clearGoogleRedirectPending();
    return { user: null, error: error.message };
  }
};

// Вспомогательная функция для сохранения/обновления пользователя
const saveOrUpdateUser = async (user) => {
    // Проверяем, существует ли пользователь в Firestore
    const userDoc = await getDoc(doc(db, 'users', user.uid));

    if (!userDoc.exists()) {
      // Новый пользователь (часто Google с вкладки «Вход»): документ без юр. полей — согласие только после чекбоксов.
      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        createdAt: new Date().toISOString(),
        lastLoginAt: new Date().toISOString(),
        displayNameOnboardingDone: false,
        preferences: {
          language: 'en',
          currency: 'USD',
          notifications: true
        },
      });
    } else {
      // Обновляем время последнего входа
      await setDoc(doc(db, 'users', user.uid), {
        lastLoginAt: new Date().toISOString()
      }, { merge: true });
  }
};

// Выход
export const logout = async () => {
  try {
    clearPendingGoogleLegalConsent();
    await signOut(auth);
    return { error: null };
  } catch (error) {
    return { error: error.message };
  }
};

// Сброс пароля (письмо ведёт на тот же SPA-путь /verify-email, что и верификация email)
export const resetPassword = async (email) => {
  try {
    const settings = buildEmailVerificationActionCodeSettings();
    if (settings) {
      await sendPasswordResetEmail(auth, email, settings);
    } else {
      await sendPasswordResetEmail(auth, email);
    }
    return { error: null };
  } catch (error) {
    return { error: error.message };
  }
};

// Получение данных пользователя из Firestore
export const getUserData = async (uid) => {
  const startTime = performance.now();
  try {
    if (!uid) {
      return { userData: null, error: 'User ID is required' };
    }
    
    const userDoc = await getDoc(doc(db, 'users', uid));
    const duration = performance.now() - startTime;

    if (userDoc.exists()) {
      return { userData: userDoc.data(), error: null };
    }
    return { userData: null, error: 'User not found' };
  } catch (error) {
    const duration = performance.now() - startTime;
    // Обрабатываем офлайн ошибки более мягко
    const errorCode = error.code || '';
    const errorMessage = error.message || '';
    const isOfflineError = 
      errorCode === 'unavailable' || 
      errorCode === 'failed-precondition' ||
      errorMessage.toLowerCase().includes('offline') ||
      errorMessage.toLowerCase().includes('client is offline');
    
    if (isOfflineError) {
      return { userData: null, error: null, offline: true };
    }
    console.warn(`[getUserData] fetch failed (${duration.toFixed(2)}ms):`, errorMessage || errorCode);
    return { userData: null, error: null, offline: false };
  }
};

// Обновление данных пользователя
export const updateUserData = async (uid, data) => {
  try {
    await setDoc(doc(db, 'users', uid), data, { merge: true });
    return { error: null };
  } catch (error) {
    return { error: error.message };
  }
};

/** Отображаемое имя: Firebase Auth profile + Firestore users/{uid}.displayName */
export const saveUserDisplayName = async (displayName, options = {}) => {
  const { completeDisplayNameOnboarding } = options;
  const user = auth.currentUser;
  if (!user) return { error: 'Not signed in', displayName: null };
  const name = String(displayName ?? '').trim();
  if (!name) return { error: 'Display name is empty', displayName: null };
  try {
    await updateProfile(user, { displayName: name });
    await setDoc(
      doc(db, 'users', user.uid),
      {
        uid: user.uid,
        email: user.email ?? null,
        displayName: name,
        lastLoginAt: new Date().toISOString(),
        ...(completeDisplayNameOnboarding ? { displayNameOnboardingDone: true } : {}),
      },
      { merge: true }
    );
    try {
      const inviteToken = getSessionInviteToken(user.uid);
      await mergeInviteLookupPublicProfile(user.uid, inviteToken, {
        displayName: name,
        photoURL: user.photoURL ?? null,
        email: user.email ?? null,
      });
    } catch (e) {
      console.warn('[saveUserDisplayName] inviteLookup profile', e);
    }
    return { error: null, displayName: name };
  } catch (error) {
    return { error: error.message || 'Failed to save', displayName: null };
  }
};

// Слушатель состояния аутентификации
export const onAuthStateChange = (callback) => {
  return onAuthStateChanged(auth, callback);
}; 